import { Injectable } from '@angular/core';
import { Observable, of, combineLatest } from 'rxjs';
import { map, publishReplay, refCount } from 'rxjs/operators';
import { UrlBuilder } from '../../core';
import { NavigationLink } from '../../navigation';
import { UserPermissionsStorage } from '../../user-permissions/user-permissions-storage';
import { UserPermissions } from '../../user-permissions/user-permissions';
import { UserStorage, User } from '../../user';

@Injectable()
export class AdminLinksInfoStorage {

    public get all() {
        return this.adminLinksInfo;
    }

    public adminLinksInfo: Observable<NavigationLink[]>;

    private readonly documentsLink = this.urlBuilder
        .esa()
        .admin()
        .uploadDocuments()
        .url();

    private readonly siteCommunicationsMassUploadLink = this.urlBuilder
        .esa()
        .globalPopup()
        .siteCommunicationsMassUpload()
        .url();

    private readonly announcementsLink = this.urlBuilder
        .esa()
        .admin()
        .announcements()
        .url();

    private readonly commonLink = this.urlBuilder
        .esa()
        .admin()
        .commonLinks()
        .url();

    private readonly protocolActivationLink = this.urlBuilder
        .esa()
        .admin()
        .protocols()
        .url();

    private readonly userAccessLink = this.urlBuilder
        .esa()
        .admin()
        .userAccess()
        .url();

    private readonly emulateUserLink = this.urlBuilder
        .esa()
        .globalPopup()
        .emulateUser()
        .url();

    private readonly auditLogsLink = this.urlBuilder
        .esa()
        .admin()
        .auditLogs()
        .url();

    private readonly kitInventoryLink = this.urlBuilder
        .esa()
        .admin()
        .kitInventory()
        .url();

    private readonly coordinatorEQueryLink = this.urlBuilder
        .esa()
        .admin()
        .coordinatorEQuery()
        .url();

    private readonly adminEQueryLink = this.urlBuilder
        .esa()
        .admin()
        .adminEQuery()
        .url();

    private readonly activityReportLink = this.urlBuilder
        .esa()
        .admin()
        .activityReports()
        .url();

    private readonly editColumns = this.urlBuilder
        .esa()
        .admin()
        .editDefaultColumns()
        .url();

    constructor(private urlBuilder: UrlBuilder, permissionsStorage: UserPermissionsStorage, userStorage: UserStorage) {
        this.adminLinksInfo = combineLatest([
            permissionsStorage.userPermissions,
            userStorage.currentUser,
        ])
        .pipe(
            map(([permissions, user]) => this.getLinks(permissions, user)),
            publishReplay(1),
            refCount()
        );
    }

    private getLinks(permissions: UserPermissions, user: User) {
        const links: NavigationLink[] = [];

        links.push(new NavigationLink('Documents', of(this.documentsLink), 'folder', null, null, of(false), ''));

        if (permissions.has('UploadSiteCommunications')) {
            links.push(new NavigationLink('Site Comm. Mass Upload', of(this.siteCommunicationsMassUploadLink), 'cloud_upload', null, null, of(false), '', true));
        }      
        if (permissions.has('UploadAnnouncements')) {
            links.push(new NavigationLink('Announcements', of(this.announcementsLink), 'message', null, null, of(false), ''));
        }
        if (permissions.has('UploadCommonLinks')) {
            links.push(new NavigationLink('Common Links', of(this.commonLink), 'insert_link', null, null, of(false), ''));
        }

        links.push(new NavigationLink('Reports', of(this.activityReportLink), 'streetview', null, null, of(false), ''));

        if (permissions.has('EQueryCreationRespondClose') || permissions.has('EQueryViewAndRespond') || permissions.has('EQueryAdministration')) {
            if (user.isInternal) {
                links.push(this.getEQueryLink(this.coordinatorEQueryLink));
            }
            if (user.isAdmin) {
                links.push(this.getEQueryLink(this.adminEQueryLink));
            }
        }

        if (permissions.has('AccessUserProfile')) {
            links.push(new NavigationLink('User Access', of(this.userAccessLink), 'security', null, null, of(false), ''));
        }
        if (permissions.has('EmulateUser')) {
            links.push(new NavigationLink('Emulate User', of(this.emulateUserLink), 'directions_run', null, null, of(false), '', true));
        }
        if (permissions.has('ProtocolAccess')) {
            links.push(new NavigationLink('Protocol Activation', of(this.protocolActivationLink), 'power_settings_new', null, null, of(false), ''));
        }   
        if (permissions.has('ProtocolReadAccess')) {
            links.push(new NavigationLink('Protocol Activation', of(this.protocolActivationLink), 'power_settings_new', null, null, of(false), ''));
        }  
        if (permissions.has('ViewAuditLogs')) {
            links.push(new NavigationLink('Audit Logs', of(this.auditLogsLink), 'track_changes', null, null, of(false), ''));
        }

        links.push(new NavigationLink('Kit Inventory', of(this.kitInventoryLink), 'card_travel', null, null, of(false), '', true));

        if (permissions.has('ConfigureViews')) {
            links.push(new NavigationLink('Configure Views/ Reports', of(this.editColumns), 'edit', null, null, of(false), ''));
        }

        return links;
    }

    private getEQueryLink(url: any[]) {
        return new NavigationLink('eQueries', of(url), 'settings_input_antenna', null, null, of(false), '', true);
    }
}